<template>
  <div class="account-recharge">
    <div class="ios-group-header">账密充值</div>

    <template v-if="step === 0">
      <div class="ios-card account-panel">
        <div class="panel-head">
          <div class="panel-title-wrap">
            <div class="panel-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21a8 8 0 0 0-16 0"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div>
              <div class="panel-title">校验卡密和账密</div>
              <div class="panel-desc">仅支持 @mail.com 邮箱，最多添加 {{ maxRows }} 条任务。</div>
            </div>
          </div>
          <div class="panel-actions">
            <button
              type="button"
              class="btn-tinted compact-button"
              :disabled="rows.length >= maxRows || validating"
              @click="addRow"
            >
              + 添加
            </button>
            <button
              type="button"
              class="btn-tinted compact-button"
              :disabled="validating"
              @click="resetInput"
            >
              重置
            </button>
          </div>
        </div>

        <div class="account-list">
          <div
            v-for="(row, index) in rows"
            :key="row.id"
            class="account-item"
          >
            <div class="item-head">
              <div class="task-title">
                <span>{{ index + 1 }}</span>
                <strong>账密任务</strong>
              </div>
              <div class="item-actions">
                <span :class="getValidateStatusClass(row)">{{ getValidateStatusText(row) }}</span>
                <button
                  v-if="rows.length > 1"
                  type="button"
                  class="link-button danger"
                  :disabled="validating"
                  @click="removeRow(index)"
                >
                  删除
                </button>
              </div>
            </div>

            <div class="field-grid">
              <div class="input-group">
                <label class="ios-field-label">卡密编号</label>
                <input
                  v-model.trim="row.cardCode"
                  class="ios-input"
                  :class="{ 'has-error': row.cardError }"
                  placeholder="例如：CARD-123456"
                  :disabled="validating || isOrderSubmitted(row)"
                  @input="clearRowValidation(row)"
                />
                <div v-if="row.cardError" class="ios-error-text">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ row.cardError }}</span>
                </div>
                <div v-else-if="row.cardInfo" class="field-success">
                  {{ row.cardInfo.productName }} · {{ row.cardInfo.cardStatusText }}
                </div>
              </div>

              <div class="input-group">
                <label class="ios-field-label">账密</label>
                <input
                  v-model.trim="row.accountText"
                  class="ios-input"
                  :class="{ 'has-error': row.accountError }"
                  placeholder="邮箱----邮箱密码"
                  :disabled="validating || isOrderSubmitted(row)"
                  @input="clearRowValidation(row)"
                />
                <div v-if="row.accountError" class="ios-error-text">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ row.accountError }}</span>
                </div>
              </div>
            </div>
            <div v-if="isOrderSubmitted(row)" class="field-success submitted-note">
              已提交订单 {{ row.orderInfo.orderId }}，再次提交不会重复创建订单。
            </div>
          </div>
        </div>

        <div v-if="error" class="ios-callout is-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>

        <button
          v-if="!allValidated"
          type="button"
          class="btn-filled"
          :disabled="!canValidate || validating"
          @click="validateAll"
        >
          <span v-if="validating" class="ios-spinner"></span>
          {{ validating ? '校验中…' : '校验全部任务' }}
        </button>
        <button
          v-else
          type="button"
          class="btn-filled btn-destructive"
          @click="submitAll"
        >
          提交账密充值
        </button>
      </div>
    </template>

    <template v-else>
      <div class="ios-card account-panel">
        <div class="panel-head">
          <div>
            <div class="panel-title">账密充值任务</div>
            <div class="panel-desc">先获取 session token，成功后自动提交充值订单。</div>
          </div>
        </div>

        <div class="ios-callout is-warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>订单未提交前请勿刷新页面，否则无法完成充值。</span>
        </div>

        <div class="progress-summary">
          <div>
            <span>任务总数</span>
            <strong>{{ rows.length }}</strong>
          </div>
          <div>
            <span>已完成</span>
            <strong class="status-completed">{{ completedCount }}</strong>
          </div>
          <div>
            <span>异常/失败</span>
            <strong class="status-error">{{ failedCount }}</strong>
          </div>
        </div>

        <div class="task-list">
          <div v-for="(row, index) in rows" :key="row.id" class="task-card">
            <div class="task-head">
              <div>
                <strong>任务 {{ index + 1 }}</strong>
                <span>{{ row.email }}</span>
              </div>
              <div class="task-status-actions">
                <span :class="getTaskStatusClass(row)">{{ getTaskStatusText(row) }}</span>
                <button
                  v-if="canRetry(row)"
                  type="button"
                  class="retry-button"
                  @click="retryTask(row)"
                >
                  重试
                </button>
                <button
                  v-if="canRetry(row)"
                  type="button"
                  class="retry-button"
                  @click="editTask(row)"
                >
                  修改
                </button>
              </div>
            </div>

            <div v-if="row.editing" class="task-edit">
              <div class="field-grid">
                <div class="input-group">
                  <label class="ios-field-label">卡密编号</label>
                  <input
                    v-model.trim="row.editCardCode"
                    class="ios-input"
                    :class="{ 'has-error': row.editCardError }"
                    placeholder="例如：CARD-123456"
                  />
                  <div v-if="row.editCardError" class="ios-error-text">
                    <span>{{ row.editCardError }}</span>
                  </div>
                </div>

                <div class="input-group">
                  <label class="ios-field-label">账密</label>
                  <input
                    v-model.trim="row.editAccountText"
                    class="ios-input"
                    :class="{ 'has-error': row.editAccountError }"
                    placeholder="邮箱----邮箱密码"
                  />
                  <div v-if="row.editAccountError" class="ios-error-text">
                    <span>{{ row.editAccountError }}</span>
                  </div>
                </div>
              </div>

              <div class="edit-actions">
                <button type="button" class="btn-gray small-action" :disabled="row.editSaving" @click="cancelEdit(row)">
                  取消
                </button>
                <button type="button" class="btn-filled small-action" :disabled="row.editSaving" @click="saveEditAndRetry(row)">
                  <span v-if="row.editSaving" class="ios-spinner"></span>
                  {{ row.editSaving ? '保存中…' : '保存并重试' }}
                </button>
              </div>
            </div>

            <div class="stage-row">
              <div class="stage-step" :class="{ active: row.stage === 'session', done: row.sessionToken }">
                <span class="stage-dot">1</span>
                <span class="stage-label">获取 Token</span>
              </div>
              <div class="stage-step" :class="{ active: row.stage === 'submit', done: row.orderInfo }">
                <span class="stage-dot">2</span>
                <span class="stage-label">提交订单</span>
              </div>
              <div class="stage-step" :class="{ active: row.stage === 'order', done: isFinalOrder(row) }">
                <span class="stage-dot">3</span>
                <span class="stage-label">充值结果</span>
              </div>
            </div>

            <div class="ios-progress-track task-progress">
              <div
                class="ios-progress-fill"
                :class="{
                  'is-completed': isCompleted(row),
                  'is-error': isFailed(row),
                  'is-returned': isReturned(row)
                }"
                :style="{ width: Math.round(row.progress) + '%' }"
              ></div>
            </div>

            <div class="task-grid">
              <div>
                <span>卡密编号</span>
                <strong>{{ row.cardCode }}</strong>
              </div>
              <div>
                <span>订单编号</span>
                <strong>{{ row.orderInfo && row.orderInfo.orderId || '-' }}</strong>
              </div>
              <div>
                <span>商品名称</span>
                <strong>{{ row.cardInfo.productName }}</strong>
              </div>
              <div>
                <span>进度</span>
                <strong>{{ Math.round(row.progress) }}%</strong>
              </div>
            </div>

            <div v-if="row.message" class="task-message" :class="{ danger: isFailed(row) }">
              {{ row.message }}
            </div>
          </div>
        </div>

        <div v-if="allTasksFinished" class="finish-actions">
          <button type="button" class="btn-filled" @click="backToInput">
            返回输入页
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  createMailLoginTask,
  createOrder,
  deleteMailLoginTask,
  queryMailLoginTask,
  queryOrderByCard,
  validateCard
} from '../services/api'
import {
  ORDER_STATUS,
  getOrderStatusClass,
  getOrderStatusText,
  normalizeOrderStatus
} from '../constants/orderStatus'

const MAX_ROWS = 5
const SESSION_POLL_INTERVAL = 3000
const ORDER_POLL_INTERVAL = 3000
const PROGRESS_INTERVAL = 200
const PROGRESS_DURATION = 30000
const SESSION_PROGRESS_MAX = 50
const ORDER_PROGRESS_START = 50
const ORDER_PROGRESS_MAX = 99

export default {
  name: 'AccountRecharge',
  data() {
    return {
      step: 0,
      rows: [this.createRow()],
      validating: false,
      error: '',
      timers: {}
    }
  },
  computed: {
    maxRows() {
      return MAX_ROWS
    },
    canValidate() {
      return this.rows.every((row) => row.cardCode.trim() && row.accountText.trim())
    },
    allValidated() {
      return this.rows.length > 0 && this.rows.every((row) => row.validated)
    },
    completedCount() {
      return this.rows.filter((row) => this.isCompleted(row)).length
    },
    failedCount() {
      return this.rows.filter((row) => this.isFailed(row) || this.isReturned(row)).length
    },
    allTasksFinished() {
      return this.rows.length > 0 && this.rows.every((row) => this.isTaskFinal(row))
    }
  },
  beforeDestroy() {
    this.stopAllTimers()
  },
  methods: {
    createRow() {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        cardCode: '',
        accountText: '',
        email: '',
        mailPassword: '',
        gptPassword: '',
        cardInfo: null,
        cardError: '',
        accountError: '',
        mailPasswordError: '',
        gptPasswordError: '',
        validated: false,
        stage: 'idle',
        sessionTaskId: '',
        sessionPollToken: '',
        sessionToken: '',
        orderInfo: null,
        orderStatus: ORDER_STATUS.PENDING,
        progress: 0,
        progressStartedAt: 0,
        message: '',
        editing: false,
        editSaving: false,
        editCardCode: '',
        editAccountText: '',
        editCardError: '',
        editAccountError: ''
      }
    },
    addRow() {
      if (this.rows.length >= MAX_ROWS) return
      this.rows.push(this.createRow())
      this.error = ''
    },
    removeRow(index) {
      this.rows.splice(index, 1)
      this.error = ''
    },
    clearRowValidation(row) {
      if (this.isOrderSubmitted(row)) return

      row.cardInfo = null
      row.cardError = ''
      row.accountError = ''
      row.mailPasswordError = ''
      row.gptPasswordError = ''
      row.email = ''
      row.mailPassword = ''
      row.gptPassword = ''
      row.validated = false
      this.error = ''
    },
    validateLocalInput() {
      this.error = ''

      const seenCards = new Set()
      const seenEmails = new Set()
      let ok = true

      this.rows.forEach((row) => {
        row.cardError = ''
        row.accountError = ''
        row.mailPasswordError = ''
        row.gptPasswordError = ''
        row.validated = false

        const account = this.parseAccountText(row.accountText.trim())
        if (account.error) {
          row.accountError = account.error
          ok = false
        } else {
          row.email = account.email
          row.mailPassword = account.mailPassword
          row.gptPassword = account.gptPassword
        }

        const cardCode = row.cardCode.trim()
        const email = row.email.trim().toLowerCase()

        if (!cardCode) {
          row.cardError = '请输入卡密'
          ok = false
        } else if (seenCards.has(cardCode)) {
          row.cardError = '卡密重复'
          ok = false
        } else {
          seenCards.add(cardCode)
        }

        if (row.accountError) {
          ok = false
        } else if (!email) {
          row.accountError = '请输入邮箱'
          ok = false
        } else if (!/^[^\s@]+@mail\.com$/i.test(email)) {
          row.accountError = '仅支持 @mail.com 邮箱'
          ok = false
        } else if (seenEmails.has(email)) {
          row.accountError = '邮箱重复'
          ok = false
        } else {
          seenEmails.add(email)
          row.email = email
        }

        if (!row.mailPassword.trim()) {
          row.mailPasswordError = '请输入邮箱密码'
          ok = false
        }
      })

      return ok
    },
    async validateAll() {
      if (!this.validateLocalInput()) return

      this.validating = true
      this.error = ''

      await Promise.all(this.rows.map((row) => this.validateRowCard(row)))

      this.validating = false

      if (!this.allValidated) {
        this.error = '部分任务校验未通过，请根据提示修改后重新校验'
      }
    },
    async validateRowCard(row) {
      if (this.isOrderSubmitted(row)) {
        row.validated = true
        return
      }

      const response = await validateCard(row.cardCode.trim())

      if (response.code === 200 && response.data && response.data.cardStatus === 1) {
        row.cardInfo = response.data
        row.validated = true
      } else {
        row.cardError = response.message || '卡密不可用'
        row.validated = false
      }
    },
    submitAll() {
      this.step = 1
      this.error = ''
      this.stopAllTimers()

      this.rows.forEach((row) => {
        if (this.isOrderSubmitted(row)) {
          this.resumeSubmittedTask(row)
        } else {
          this.startTask(row)
        }
      })
    },
    startTask(row, options = {}) {
      const reusableSessionToken = options.reuseSessionToken ? row.sessionToken : ''

      this.stopRowTimers(row)
      row.stage = reusableSessionToken ? 'submit' : 'session'
      row.message = reusableSessionToken
        ? '已复用 session token，正在提交充值订单'
        : '正在提交获取 session token 任务'
      row.progress = reusableSessionToken ? ORDER_PROGRESS_START : 3
      row.progressStartedAt = Date.now()
      row.sessionTaskId = ''
      row.sessionPollToken = ''
      row.sessionToken = reusableSessionToken
      row.orderInfo = null
      row.orderStatus = ORDER_STATUS.PENDING
      this.startProgress(row)

      if (reusableSessionToken) {
        this.submitOrder(row)
      } else {
        this.startSessionTask(row)
      }
    },
    resumeSubmittedTask(row) {
      this.stopRowTimers(row)
      row.stage = 'order'
      row.message = this.isFinalOrder(row)
        ? getOrderStatusText(row.orderStatus)
        : '订单已提交，正在恢复充值结果轮询'
      row.progressStartedAt = Date.now()

      if (this.isFinalOrder(row)) {
        row.progress = 100
      } else {
        row.progress = Math.min(Math.max(row.progress, ORDER_PROGRESS_START), ORDER_PROGRESS_MAX)
        this.startProgress(row)
        this.startOrderPolling(row)
      }
    },
    retryTask(row) {
      row.editing = false
      this.startTask(row, { reuseSessionToken: true })
    },
    editTask(row) {
      this.stopRowTimers(row)
      row.editing = true
      row.editSaving = false
      row.editCardCode = row.cardCode
      row.editAccountText = row.accountText || `${row.email}----${row.mailPassword}`
      row.editCardError = ''
      row.editAccountError = ''
    },
    cancelEdit(row) {
      row.editing = false
      row.editSaving = false
      row.editCardError = ''
      row.editAccountError = ''
    },
    parseAccountText(accountText) {
      const match = accountText.trim().match(/^(.+?@mail\.com)-{3,}(.+)$/i)
      if (!match) {
        return {
          error: '格式应为：邮箱----邮箱密码'
        }
      }

      const email = match[1].trim().toLowerCase()
      const mailPassword = match[2].trim()
      if (!/^[^\s@]+@mail\.com$/i.test(email)) {
        return {
          error: '仅支持 @mail.com 邮箱'
        }
      }

      if (!mailPassword) {
        return {
          error: '请输入邮箱密码'
        }
      }

      return {
        email,
        mailPassword,
        gptPassword: 'null',
        error: ''
      }
    },
    async saveEditAndRetry(row) {
      row.editCardError = ''
      row.editAccountError = ''

      const cardCode = row.editCardCode.trim()
      if (!cardCode) {
        row.editCardError = '请输入卡密'
        return
      }

      const account = this.parseAccountText(row.editAccountText.trim())
      if (account.error) {
        row.editAccountError = account.error
        return
      }

      row.editSaving = true
      const cardResponse = await validateCard(cardCode)
      row.editSaving = false

      if (cardResponse.code !== 200 || !cardResponse.data || cardResponse.data.cardStatus !== 1) {
        row.editCardError = cardResponse.message || '卡密不可用'
        return
      }

      const accountChanged = row.email !== account.email || row.mailPassword !== account.mailPassword

      row.cardCode = cardCode
      row.accountText = row.editAccountText.trim()
      row.email = account.email
      row.mailPassword = account.mailPassword
      row.gptPassword = account.gptPassword
      if (accountChanged) {
        row.sessionToken = ''
      }
      row.cardInfo = cardResponse.data
      row.cardError = ''
      row.accountError = ''
      row.mailPasswordError = ''
      row.gptPasswordError = ''
      row.validated = true
      row.editing = false
      this.startTask(row, { reuseSessionToken: !accountChanged })
    },
    async startSessionTask(row) {
      const response = await createMailLoginTask({
        email: row.email.trim(),
        mailPassword: row.mailPassword,
        gptPassword: row.gptPassword
      })

      if (response.ok && response.task_id && response.poll_token) {
        row.sessionTaskId = response.task_id
        row.sessionPollToken = response.poll_token
        row.message = response.queue_position
          ? `获取 token 排队中，当前位置 ${response.queue_position}`
          : '正在获取 session token'
        this.startSessionPolling(row)
      } else {
        row.stage = 'failed'
        row.message = response.error || '获取 session token 任务提交失败'
        row.progress = 100
        this.stopRowTimers(row)
      }
    },
    startSessionPolling(row) {
      const sessionTimer = setInterval(() => {
        this.pollSessionTask(row)
      }, SESSION_POLL_INTERVAL)

      this.setRowTimer(row, { sessionTimer })
      this.pollSessionTask(row)
    },
    async pollSessionTask(row) {
      if (!row.sessionTaskId || !row.sessionPollToken || row.stage !== 'session') return

      const response = await queryMailLoginTask(row.sessionTaskId, row.sessionPollToken)

      if (response.ok && ['queued', 'running'].includes(response.status)) {
        row.message = response.status === 'queued'
          ? `获取 token 排队中${response.queue_position ? `，当前位置 ${response.queue_position}` : ''}`
          : '正在登录并获取 session token'
        return
      }

      if (response.ok && response.status === 'success' && response.result && response.result.access_token) {
        row.sessionToken = this.buildTokenPayload(response.result)
        row.message = '已获取 session token，正在提交充值订单'
        this.clearRowTimer(row, 'sessionTimer')
        await deleteMailLoginTask(row.sessionTaskId, row.sessionPollToken)
        this.submitOrder(row)
        return
      }

      row.stage = 'failed'
      row.message = response.error || '获取 session token 失败'
      this.clearRowTimer(row, 'sessionTimer')
      if (row.sessionTaskId && row.sessionPollToken) {
        await deleteMailLoginTask(row.sessionTaskId, row.sessionPollToken)
      }
      row.progress = 100
      this.stopRowTimers(row)
    },
    buildTokenPayload(result) {
      const sessionData = result.session_data && typeof result.session_data === 'object'
        ? result.session_data
        : {}
      const user = {
        ...(sessionData.user || {}),
        email: result.email
      }

      return JSON.stringify({
        ...sessionData,
        accessToken: result.access_token,
        access_token: result.access_token,
        user
      })
    },
    async submitOrder(row) {
      row.stage = 'submit'
      row.message = '正在提交充值订单'

      const response = await createOrder(row.cardCode.trim(), row.sessionToken)

      if (response.code === 200 && response.data) {
        row.orderInfo = response.data
        row.orderStatus = normalizeOrderStatus(response.data.status)
        row.stage = 'order'
        row.message = '订单已提交，正在等待充值结果'
        row.progress = Math.max(row.progress, ORDER_PROGRESS_START)
        row.progressStartedAt = Date.now()
        if (this.isFinalOrder(row)) {
          row.progress = 100
          this.stopRowTimers(row)
        } else {
          this.startOrderPolling(row)
        }
      } else {
        row.stage = 'failed'
        row.orderStatus = ORDER_STATUS.EXCEPTION
        row.message = response.message || '订单提交失败'
        row.progress = 100
        this.stopRowTimers(row)
      }
    },
    startOrderPolling(row) {
      const orderTimer = setInterval(() => {
        this.pollOrder(row)
      }, ORDER_POLL_INTERVAL)

      this.setRowTimer(row, { orderTimer })
      this.pollOrder(row)
    },
    async pollOrder(row) {
      if (row.stage !== 'order') return

      const response = await queryOrderByCard(row.cardCode.trim())

      if (response.code === 200 && response.data) {
        row.orderInfo = {
          ...row.orderInfo,
          ...response.data
        }
        row.orderStatus = normalizeOrderStatus(row.orderInfo.status)
        row.message = '订单状态已更新'

        if (this.isFinalOrder(row)) {
          row.progress = 100
          row.message = getOrderStatusText(row.orderStatus)
          this.stopRowTimers(row)
        }
      } else if (response.data && response.data.found === false) {
        row.orderStatus = ORDER_STATUS.CARD_RETURNED
        row.stage = 'order'
        row.message = response.data.message || response.message || '充值失败，卡密已退回'
        row.progress = 100
        this.stopRowTimers(row)
      } else {
        row.message = response.message || '订单结果查询失败，系统将继续重试'
      }
    },
    startProgress(row) {
      const progressTimer = setInterval(() => {
        if (this.isTaskFinal(row)) {
          row.progress = 100
          this.clearRowTimer(row, 'progressTimer')
          return
        }

        const elapsed = Date.now() - row.progressStartedAt
        if (row.stage === 'order') {
          const orderRange = ORDER_PROGRESS_MAX - ORDER_PROGRESS_START
          const orderProgress = ORDER_PROGRESS_START + Math.min(orderRange, (elapsed / PROGRESS_DURATION) * orderRange)
          row.progress = Math.max(row.progress, orderProgress)
          return
        }

        row.progress = Math.max(row.progress, Math.min(SESSION_PROGRESS_MAX, (elapsed / PROGRESS_DURATION) * SESSION_PROGRESS_MAX))
      }, PROGRESS_INTERVAL)

      this.setRowTimer(row, { progressTimer })
    },
    setRowTimer(row, timerPatch) {
      this.$set(this.timers, row.id, {
        ...(this.timers[row.id] || {}),
        ...timerPatch
      })
    },
    clearRowTimer(row, timerName) {
      const timer = this.timers[row.id] && this.timers[row.id][timerName]
      if (!timer) return
      clearInterval(timer)
      this.$delete(this.timers[row.id], timerName)
    },
    stopRowTimers(row) {
      const timer = this.timers[row.id]
      if (!timer) return

      Object.keys(timer).forEach((key) => {
        clearInterval(timer[key])
      })
      this.$delete(this.timers, row.id)
    },
    stopAllTimers() {
      Object.keys(this.timers).forEach((id) => {
        Object.keys(this.timers[id]).forEach((key) => {
          clearInterval(this.timers[id][key])
        })
      })
      this.timers = {}
    },
    resetAll() {
      this.stopAllTimers()
      this.step = 0
      this.rows = [this.createRow()]
      this.validating = false
      this.error = ''
    },
    resetInput() {
      this.stopAllTimers()
      this.rows = [this.createRow()]
      this.validating = false
      this.error = ''
    },
    backToInput() {
      this.stopAllTimers()
      this.step = 0
      this.rows.forEach((row) => {
        if (!this.isOrderSubmitted(row)) {
          row.stage = 'idle'
          row.sessionTaskId = ''
          row.sessionPollToken = ''
          row.orderStatus = ORDER_STATUS.PENDING
          row.progress = 0
          row.progressStartedAt = 0
          row.message = ''
        }
        row.editing = false
        row.editSaving = false
      })
      this.error = ''
    },
    getValidateStatusText(row) {
      if (row.validated) return '校验通过'
      if (row.cardError || row.accountError || row.mailPasswordError || row.gptPasswordError) return '校验失败'
      return '待校验'
    },
    getValidateStatusClass(row) {
      if (row.validated) return 'status-available'
      if (row.cardError || row.accountError || row.mailPasswordError || row.gptPasswordError) return 'status-error'
      return 'status-unknown'
    },
    getTaskStatusText(row) {
      if (row.stage === 'session') return '获取 Token'
      if (row.stage === 'submit') return '提交订单'
      if (row.stage === 'failed') return '失败'
      if (row.stage === 'order') return getOrderStatusText(row.orderStatus)
      return '等待中'
    },
    getTaskStatusClass(row) {
      if (row.stage === 'failed') return 'status-error'
      if (row.stage === 'session' || row.stage === 'submit') return 'status-processing'
      return getOrderStatusClass(row.orderStatus)
    },
    isCompleted(row) {
      return normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.COMPLETED
    },
    isReturned(row) {
      return normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.CARD_RETURNED
    },
    isFailed(row) {
      return row.stage === 'failed' || normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.EXCEPTION
    },
    isFinalOrder(row) {
      return this.isCompleted(row) || this.isReturned(row) || normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.EXCEPTION
    },
    isTaskFinal(row) {
      return row.stage === 'failed' || this.isFinalOrder(row)
    },
    canRetry(row) {
      return row.stage === 'failed' && !this.isOrderSubmitted(row)
    },
    isOrderSubmitted(row) {
      return !!(row.orderInfo && row.orderInfo.orderId)
    }
  }
}
</script>

<style scoped>
.account-panel {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-head,
.item-head,
.task-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.panel-title-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: rgba(175, 82, 222, 0.1);
  color: var(--ios-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.panel-icon svg {
  width: 21px;
  height: 21px;
}

.panel-title {
  font-size: 19px;
  font-weight: 600;
}

.panel-desc {
  margin-top: 3px;
  font-size: 14px;
  color: var(--ios-label-secondary);
}

.compact-button {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 11px;
  flex-shrink: 0;
}

.compact-button:disabled {
  color: var(--ios-label-tertiary);
  background: var(--ios-fill);
  cursor: not-allowed;
}

.account-list,
.task-list {
  display: flex;
  flex-direction: column;
}

.account-item,
.task-card {
  padding: 16px 0;
  border-radius: 0;
  background: transparent;
}

.account-item + .account-item,
.task-card + .task-card {
  border-top: 1px solid var(--ios-separator);
}

.task-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.task-title span {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--ios-fill);
  color: var(--ios-label-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.task-title strong {
  font-size: 15px;
  font-weight: 600;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  font-weight: 600;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 14px;
  margin-top: 12px;
}

.input-group {
  min-width: 0;
}

.account-panel .ios-input {
  padding: 12px 14px;
  font-size: 15px;
  border-radius: 11px;
}

.account-panel .ios-textarea {
  padding: 12px 14px;
  font-size: 15px;
  border-radius: 11px;
}

.field-success {
  margin-top: 8px;
  font-size: 13px;
  color: var(--ios-green);
  word-break: break-all;
}

.link-button {
  border: none;
  background: transparent;
  color: var(--ios-blue);
  font: inherit;
  cursor: pointer;
}

.link-button.danger {
  color: var(--ios-red);
}

.progress-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.progress-summary div {
  padding: 10px 12px;
  border-radius: 11px;
  background: rgba(118, 118, 128, 0.08);
}

.progress-summary span,
.task-grid span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--ios-label-secondary);
}

.progress-summary strong {
  font-size: 20px;
  line-height: 1;
}

.task-head > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.task-head span {
  font-size: 13px;
  color: var(--ios-label-secondary);
  word-break: break-all;
}

.task-status-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.task-status-actions > span {
  font-weight: 700;
}

.retry-button {
  min-height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 10px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--ios-blue);
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.retry-button:active {
  background: rgba(0, 122, 255, 0.22);
}

.task-edit {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(118, 118, 128, 0.08);
}

.task-edit .field-grid {
  margin-top: 0;
}

.edit-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.small-action {
  min-height: 40px;
  border-radius: 12px;
  font-size: 15px;
}

.edit-actions .btn-gray,
.edit-actions .btn-filled {
  flex: 1;
}

.stage-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  margin-top: 14px;
  padding: 4px 2px 0;
}

.stage-step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  color: var(--ios-label-secondary);
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  min-width: 0;
}

.stage-step::before,
.stage-step::after {
  content: '';
  position: absolute;
  top: 12px;
  height: 2px;
  background: var(--ios-separator);
}

.stage-step::before {
  left: 0;
  right: 50%;
}

.stage-step::after {
  left: 50%;
  right: 0;
}

.stage-step:first-child::before,
.stage-step:last-child::after {
  display: none;
}

.stage-dot {
  position: relative;
  z-index: 1;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid var(--ios-separator);
  background: var(--ios-card);
  color: var(--ios-label-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.stage-label {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-step.active {
  color: var(--ios-blue);
}

.stage-step.active .stage-dot {
  border-color: var(--ios-blue);
  background: rgba(0, 122, 255, 0.1);
  color: var(--ios-blue);
}

.stage-step.done {
  color: var(--ios-green);
}

.stage-step.done::before,
.stage-step.done::after {
  background: rgba(52, 199, 89, 0.55);
}

.stage-step.done .stage-dot {
  border-color: var(--ios-green);
  background: var(--ios-green);
  color: #fff;
}

.stage-step.active::before {
  background: rgba(52, 199, 89, 0.55);
}

.task-progress {
  margin-top: 10px;
}

.ios-progress-fill.is-completed {
  background: var(--ios-green);
}

.ios-progress-fill.is-error {
  background: var(--ios-red);
}

.ios-progress-fill.is-returned {
  background: var(--ios-orange);
}

.task-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-top: 10px;
}

.task-grid strong {
  display: block;
  font-size: 14px;
  word-break: break-all;
}

.task-message {
  margin-top: 10px;
  padding: 9px 11px;
  border-radius: 10px;
  background: rgba(255, 149, 0, 0.12);
  color: #8a5300;
  font-size: 13px;
  line-height: 1.45;
}

.task-message.danger {
  background: rgba(255, 59, 48, 0.08);
  color: #c0271d;
}

.finish-actions {
  display: flex;
  margin-top: 4px;
}

.finish-actions .btn-filled {
  width: 100%;
}

@media (max-width: 600px) {
  .account-panel {
    padding: 16px 14px;
  }

  .panel-head,
  .field-grid {
    align-items: stretch;
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .panel-actions {
    width: 100%;
  }

  .panel-title-wrap {
    align-items: flex-start;
  }

  .compact-button {
    flex: 1;
  }

  .progress-summary,
  .task-grid {
    grid-template-columns: 1fr;
  }
}
</style>
